import {
  Avatar,
  Badge,
  Box,
  Button,
  Container,
  Divider,
  Heading,
  HStack,
  Icon,
  Link as ChakraLink,
  Text,
  VStack,
  Wrap,
  useColorModeValue,
} from '@chakra-ui/react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { FiArrowLeft, FiExternalLink } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { usePublicUser } from '../../hooks/useBounties';
import { useUserApprovedClaims } from '../../hooks/useClaims';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ReputationBadge } from '../common/ReputationBadge';
import { formatDate, RESEARCH_TYPES } from '../../utils/formatters';
import { maskReputation } from '../../utils/anonymizer';
import { NewMessageButton } from '../messages/NewMessageButton';

export default function UserProfile() {
  const { userId } = useParams();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { user, loading: userLoading } = usePublicUser(userId);
  const { claims, loading: claimsLoading } = useUserApprovedClaims(userId);

  const cameFrom = location.state?.from;
  const cameFromBounties = cameFrom === 'bounties';
  const cameFromResearchers = cameFrom === 'researchers';

  const cardBg = useColorModeValue('white', 'gray.800');
  const metaBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  if (userLoading || claimsLoading) return <LoadingSpinner message="Loading profile..." />;

  if (!user) {
    return (
      <Container maxW="3xl" py={8} textAlign="center">
        <Heading size="md" mb={4}>User not found</Heading>
        <Button as={Link} to={cameFromResearchers ? '/researchers' : '/tasks'} leftIcon={<Icon as={FiArrowLeft} />}>
          {cameFromResearchers ? 'Back to Researchers' : 'Back to Tasks'}
        </Button>
      </Container>
    );
  }

  const isOwnProfile = currentUser?.uid === userId;
  const isAnonymous = user.isFullyAnonymous === true && !isOwnProfile;

  const specialties = user.specialties || [];
  const rep = user.reputation;

  return (
    <Container maxW="3xl" py={8}>
      <VStack spacing={8} align="stretch">
        {(cameFromBounties || cameFromResearchers) && (
          <Button
            as={Link}
            to={cameFromResearchers ? '/researchers' : '/tasks'}
            variant="ghost"
            leftIcon={<Icon as={FiArrowLeft} />}
            alignSelf="flex-start"
            size="sm"
          >
            {cameFromResearchers ? 'Back to Researchers' : 'Back to Tasks'}
          </Button>
        )}

        {/* Profile header */}
        <Box bg={cardBg} rounded="xl" p={8} borderWidth={1} borderColor={borderColor}>
          <VStack spacing={6} align="center">
            <Avatar
              size="2xl"
              src={isAnonymous ? undefined : user.avatarUrl}
              name={isAnonymous ? 'Anonymous' : (user.anonymizedUsername || 'User')}
              bg={isAnonymous ? 'gray.400' : 'orange.400'}
            />
            <VStack spacing={2}>
              <Heading size="lg">{isAnonymous ? 'Anonymous' : user.anonymizedUsername}</Heading>
              {!isAnonymous && user.createdAt && (
                <Text fontSize="sm" color="gray.500">
                  Member since {formatDate(user.createdAt)}
                </Text>
              )}
            </VStack>

            {/* Reputation badge */}
            {rep && (
              <VStack spacing={2}>
                <ReputationBadge
                  reputation={isAnonymous ? maskReputation(rep) : rep}
                  anonymous={isAnonymous}
                />
                {!isAnonymous && (
                  <Text fontSize="sm" color="gray.600">
                    {rep.completedBounties || 0} tasks completed
                  </Text>
                )}
              </VStack>
            )}
            {!isOwnProfile && (
              <NewMessageButton targetUid={userId} />
            )}
          </VStack>
        </Box>

        {isAnonymous ? (
          <Box bg={cardBg} rounded="xl" p={6} borderWidth={1} borderColor={borderColor} textAlign="center">
            <Text fontSize="sm" color="gray.500">
              This user has enabled Fully Anonymous Mode.
            </Text>
          </Box>
        ) : (
          <>
            {/* Research Specialties */}
            {specialties.length > 0 && (
              <Box bg={cardBg} rounded="xl" p={6} borderWidth={1} borderColor={borderColor}>
                <VStack align="stretch" spacing={4}>
                  <Heading size="sm">Research Specialties</Heading>
                  <Divider />
                  <Wrap spacing={2}>
                    {specialties.map((key) => {
                      const typeInfo = RESEARCH_TYPES[key] || { label: key, color: 'gray' };
                      return (
                        <Badge
                          key={key}
                          colorScheme={typeInfo.color}
                          fontSize="sm"
                          px={3}
                          py={1}
                          rounded="md"
                        >
                          {typeInfo.label}
                        </Badge>
                      );
                    })}
                  </Wrap>
                </VStack>
              </Box>
            )}

            {/* Completed Research */}
            <Box bg={cardBg} rounded="xl" p={6} borderWidth={1} borderColor={borderColor}>
              <VStack align="stretch" spacing={4}>
                <Heading size="sm">Completed Research</Heading>
                <Divider />
                {claims.length === 0 ? (
                  <Text fontSize="sm" color="gray.500" py={4} textAlign="center">
                    No completed research yet.
                  </Text>
                ) : (
                  <VStack align="stretch" spacing={0} divider={<Divider />}>
                    {claims.map((claim) => (
                      <HStack
                        key={claim.id}
                        justify="space-between"
                        py={3}
                        _hover={{ bg: metaBg }}
                        px={2}
                        rounded="md"
                        transition="background 0.2s"
                      >
                        <VStack align="start" spacing={0} flex="1">
                          <ChakraLink
                            as={Link}
                            to={`/submissions/${claim.id}`}
                            fontWeight="medium"
                            fontSize="sm"
                            _hover={{ color: 'orange.500', textDecoration: 'underline' }}
                          >
                            {claim.bountyTitle || 'Research Submission'}
                          </ChakraLink>
                          <Text fontSize="xs" color="gray.500">
                            Completed {formatDate(claim.reviewedAt)}
                          </Text>
                        </VStack>
                        <Icon as={FiExternalLink} color="gray.400" boxSize={4} />
                      </HStack>
                    ))}
                  </VStack>
                )}
              </VStack>
            </Box>
          </>
        )}
      </VStack>
    </Container>
  );
}
